import { ReviewAboutSection, ReviewInfoData, ReviewInfoSection } from '@/src/types/hotels/details'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Divider, Typography } from '@mui/material'

export interface HotelInfoPolicyRow {
    label: string
    value: string
}

export interface HotelInfoSectionsProps {
    info: ReviewInfoData
    labels: {
        overview: string
        location: string
        attractions: string
        hotelPolicies: string
    }
    /** USP chips from roomContent (localized). Empty if none. */
    tags: string[]
    /** Pre-formatted property facts, e.g. ["120 Rooms", "8 Floors"]. Empty if none. */
    propertyFacts: string[]
    /** Parsed about-hotel blocks (bold label + body), sliced for the collapsed state. */
    aboutSections: ReviewAboutSection[]
    hasMoreAbout: boolean
    aboutToggleLabel: string
    onToggleAbout: () => void
    facilitySections: ReviewInfoSection[]
    policyRows: HotelInfoPolicyRow[]
    /** Open every accordion on first render. */
    expandAll?: boolean
}

// Pure presentation: overview/about, attractions, and facility + policy accordions.
const HotelInfoSections = ({
    info,
    labels,
    tags,
    propertyFacts,
    aboutSections,
    hasMoreAbout,
    aboutToggleLabel,
    onToggleAbout,
    facilitySections,
    policyRows,
    expandAll = false
}: HotelInfoSectionsProps) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {(tags.length > 0 || propertyFacts.length > 0) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {tags.map(tag => (
                            <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                sx={{
                                    bgcolor: 'neutral.100',
                                    color: 'textColors.primary',
                                    fontSize: 12,
                                    fontWeight: 500
                                }}
                            />
                        ))}
                    </Box>
                )}
                {propertyFacts.length > 0 && (
                    <Typography sx={{ fontSize: 13, color: 'textColors.secondary' }}>
                        {propertyFacts.join(' · ')}
                    </Typography>
                )}
            </Box>
        )}

        {(info.about.length > 0 || info.location) && (
            <Box sx={{ bgcolor: 'neutral.white', border: 1, borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
                <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700, color: 'textColors.primary', m: 0 }}>
                    {labels.overview}
                </Typography>

                {info.about.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                        {aboutSections.map((section, index) => (
                            <Box key={index} sx={{ mt: index === 0 ? 0 : 1 }}>
                                {section.label && (
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'textColors.primary' }}>
                                        {section.label}
                                    </Typography>
                                )}
                                {section.body && (
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            color: 'textColors.secondary',
                                            mt: section.label ? 0.25 : 0,
                                            lineHeight: 1.5
                                        }}
                                    >
                                        {section.body}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                        {hasMoreAbout && (
                            <Box
                                component="button"
                                type="button"
                                onClick={onToggleAbout}
                                sx={{ border: 0, bgcolor: 'transparent', p: 0, mt: 0.5, cursor: 'pointer' }}
                            >
                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'link.main' }}>
                                    {aboutToggleLabel}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {info.location && (
                    <Box sx={{ mt: 1.5 }}>
                        <Typography
                            component="h3"
                            sx={{ fontSize: 14, fontWeight: 600, color: 'textColors.primary', m: 0 }}
                        >
                            {labels.location}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'textColors.secondary', mt: 0.5, lineHeight: 1.5 }}>
                            {info.location}
                        </Typography>
                    </Box>
                )}
            </Box>
        )}

        {info.attractions.length > 0 && (
            <Box sx={{ bgcolor: 'neutral.white', border: 1, borderColor: 'divider', borderRadius: 1.5, p: 2 }}>
                <Typography
                    component="h2"
                    sx={{ fontSize: 16, fontWeight: 700, color: 'textColors.primary', mb: 1, mt: 0 }}
                >
                    {labels.attractions}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {info.attractions.map(item => (
                        <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PlaceOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography sx={{ fontSize: 13, color: 'textColors.secondary' }}>{item}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        )}

        {(facilitySections.length > 0 || policyRows.length > 0) && (
            <Box
                sx={{
                    bgcolor: 'neutral.white',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    overflow: 'hidden'
                }}
            >
                {facilitySections.map(section => (
                    <Accordion key={section.key} disableGutters elevation={0} square defaultExpanded={expandAll}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'textColors.primary' }}>
                                {section.title}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {section.items.map(item => (
                                    <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                        <Typography sx={{ fontSize: 13, color: 'textColors.secondary' }}>
                                            {item}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))}

                {policyRows.length > 0 && (
                    <Accordion disableGutters elevation={0} square defaultExpanded={expandAll}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'textColors.primary' }}>
                                {labels.hotelPolicies}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {policyRows.map((row, index) => (
                                    <Box key={row.label}>
                                        {index > 0 && <Divider sx={{ mb: 1 }} />}
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'textColors.primary' }}>
                                            {row.label}
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, color: 'textColors.secondary', mt: 0.25 }}>
                                            {row.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                )}
            </Box>
        )}
    </Box>
)

export { HotelInfoSections }
export default HotelInfoSections
